import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import translations from '../i18n/translations';

const LanguageContext = createContext(null);

const SUPPORTED = ['fr', 'ar'];
const DEFAULT_LANG = 'fr';

const getInitialLang = () => {
  try {
    const stored = localStorage.getItem('gofit_lang');
    if (stored && SUPPORTED.includes(stored)) return stored;
  } catch {}
  return DEFAULT_LANG;
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(getInitialLang);
  const dir = lang === 'ar' ? 'rtl' : 'ltr';

  useEffect(() => {
    document.documentElement.setAttribute('lang', lang);
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.classList.toggle('rtl', dir === 'rtl');
    try { localStorage.setItem('gofit_lang', lang); } catch {}
  }, [lang, dir]);

  const setLanguage = useCallback((next) => {
    if (SUPPORTED.includes(next)) setLang(next);
  }, []);

  const toggleLanguage = useCallback(() => {
    setLang((prev) => (prev === 'fr' ? 'ar' : 'fr'));
  }, []);

  // Resolve a dot-path like 'hero.ctaCollection' against the current dictionary
  const t = useCallback((path, fallback) => {
    const parts = String(path).split('.');
    let node = translations[lang];
    for (const part of parts) {
      if (node == null) return fallback ?? path;
      node = node[part];
    }
    return node ?? fallback ?? path;
  }, [lang]);

  const value = useMemo(() => ({ lang, dir, setLanguage, toggleLanguage, t }), [lang, dir, setLanguage, toggleLanguage, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    // Safe fallback so components don't crash if provider is missing
    return {
      lang: DEFAULT_LANG,
      dir: 'ltr',
      setLanguage: () => {},
      toggleLanguage: () => {},
      t: (p, f) => f ?? p,
    };
  }
  return ctx;
};

export default LanguageContext;
